import React, { memo } from 'react'

import {
  useTicksMarketDataEvents,
  useTicksMarketDataSubscription,
  type TicksMarketDataEvent,
} from '@features/websocket'

import { useFeatureFlags } from '@context/FeatureFlagsContext'

import { updateCurrentPrices } from '../hooks/useCurrentPricesStore'
import {
  isTicksMarketDataMessage,
  useMarketDataWebSocket,
  type MarketDataMessage,
} from '../hooks/useMarketDataWebSocket'

type Props = {
  symbols?: string[]
  onTick?: (message: MarketDataMessage) => void
}

const toLegacyMarketDataMessage = (event: TicksMarketDataEvent): MarketDataMessage => ({
  $type: 'TicksMarketData',
  Ticks: event.Data.Ticks.map((tick) => ({ ...tick, Symbol: event.Data.Symbol })),
})

export const updateCurrentPricesFromMarketData = (message: MarketDataMessage): void => {
  if (!isTicksMarketDataMessage(message)) return

  updateCurrentPrices(
    Object.fromEntries(
      message.Ticks.map(({ Symbol, Bid, Ask }) => [Symbol, { bid: Bid, ask: Ask }])
    )
  )
}

const LegacyMarketDataBridge: React.FC<Props> = ({ symbols, onTick }) => {
  useMarketDataWebSocket({ symbols, onTick })
  return null
}

const NotificationsHubMarketDataBridge: React.FC<Props> = ({ symbols, onTick }) => {
  const handleEvent = (event: TicksMarketDataEvent) => onTick?.(toLegacyMarketDataMessage(event))

  useTicksMarketDataEvents(handleEvent, { enabled: symbols === undefined })
  useTicksMarketDataSubscription(symbols ?? [], {
    enabled: symbols !== undefined,
    onEvent: handleEvent,
  })

  return null
}

const MarketDataBridgeComponent: React.FC<Props> = (props) => {
  const { marketDataWSType } = useFeatureFlags()

  return marketDataWSType === 'notifications-hub' ? (
    <NotificationsHubMarketDataBridge {...props} />
  ) : (
    <LegacyMarketDataBridge {...props} />
  )
}

export const MarketDataBridge = memo(MarketDataBridgeComponent)
